import {
    AfterViewInit,
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component, ContentChildren,
    ElementRef,
    EventEmitter, Host,
    HostListener,
    Input, OnDestroy,
    OnInit, Optional,
    Output, QueryList, Renderer2, SkipSelf, ViewChild, ViewChildren, ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import _ from 'lodash';
import {ControlContainer} from '../../interfaces/ControlContainer'
import {ControlAccessor} from '../../models/ControlAccessor';
import {AzoraSlot, SlotState} from '@azora/core';
import {untilDestroyed} from '@azora/common';
import {CdkOverlayOrigin, ConnectionPositionPair, Overlay, OverlayConfig, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {SelectOption} from "../control-group/option";


@Component({
    selector: 'az-select',
    templateUrl: './select.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class AzoraSelect extends ControlAccessor implements OnInit, AfterViewInit, OnDestroy {
    
    ///-----------------------------------------------  Variables   -----------------------------------------------///
    show_menu: Boolean = false;
    selected_option: any = null;
    @Input() options: any = null;
    @Input() value = null;
    @Output() change = new EventEmitter();
    @Output() blur = new EventEmitter();
    @Input() invalid: Boolean = false;
    
    @Input('placeholder')
    private _placeholder = '';
    
    get context(): object {
        return {
            $implicit: {
                value: this.control.value,
                dirty: this.control.dirty,
                valid: this.control.valid,
                invalid: this.control.invalid,
                props: this.control.props
            }
        };
    }
    
    
    templates = {
        prepend: null,
        append: null,
        before: null,
        after: null,
        clearable: null,
        feedback_icons: null
    };
    
    /** Whether or not the overlay menu is open. */
    private _menuOpen = false;
    
    protected overlayRef: OverlayRef;
    
    
    
    
    /**
     *  Template for AzInput content.
     * @docs-private
     */
    @ViewChildren(AzoraSlot)
    private _implicitContent: QueryList<AzoraSlot>;
    
    /**
     * Template provided inside the AzInput view that will be used if present
     * @docs-private
     */
    @ContentChildren(AzoraSlot)
    private _explicitContent: QueryList<AzoraSlot>;
    
    @ViewChild('menu', {static: true})
    private _menuTemplate;
    
    @ViewChild(CdkOverlayOrigin, {static: true})
    private _menuTrigger: CdkOverlayOrigin;
    
    constructor(
            private _viewContainerRef: ViewContainerRef,
            private _changeDetectorRef: ChangeDetectorRef,
            private _elementRef: ElementRef,
            private _renderer: Renderer2,
            private _overlay: Overlay,
            @Optional() @Host() @SkipSelf()  parent: ControlContainer
    ) {
        super(_elementRef, _changeDetectorRef);
        this.parent = parent;
    }
    
    ngOnInit(): void {
        super.ngOnInit();
        
        if (!this.root) {
            
            this.control.setControlState({
                value: this.value,
                props: {
                    label: this.label,
                    placeholder: this._placeholder,
                    type: this.type,
                    clearable: this.clearable
                },
                schema: this.schema
            });
        }
        
    }
    
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        
        _.each(this._implicitContent.toArray(), (content: AzoraSlot) => this._loadTemplate(content));
        
        _.each(this._explicitContent.toArray(), (content: AzoraSlot) => this._loadTemplate(content));
        
    }
    
    ngOnDestroy(): void {
    }
    
    
    onFocus = () => {
        if (!this.disabled) {
            this.control.markAsFocused()
            this.focus.emit(true)
        }
    }
    onBlur = () => {
        // if(!this.disabled) {
        this.control.markAsBlurred()
        this.blur.emit(true)
        // }
    }
    
    /** Toggles the overlay panel open or closed. */
    toggle(): void {
        this._menuOpen ? this.close() : this.open();
    }
    
    /** Opens the overlay menu. */
    open(): void {
        if (this.disabled || !this.options || !this.options.length || this._menuOpen) {
            return;
        }
        
        this.onFocus()
        
        // this._triggerRect = this.trigger.nativeElement.getBoundingClientRect();
        // Note: The computed font-size will be a string pixel value (e.g. "16px").
        // `parseInt` ignores the trailing 'px' and converts this to a number.
        // this._triggerFontSize = parseInt(getComputedStyle(this.trigger.nativeElement).fontSize || '0');
        
        this._menuOpen = true;
        // this._keyManager.withHorizontalOrientation(null);
        // this._calculateOverlayPosition();
        // this._highlightCorrectOption();
        this._changeDetectorRef.markForCheck();
        
        this.overlayRef = this._overlay.create(this.getOverlayConfig());
        this.overlayRef.attach(new TemplatePortal(this._menuTemplate, this._viewContainerRef));
        
        // Set the font size on the panel element once it exists.
        // this._ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
        //   if (this._triggerFontSize && this.overlayDir.overlayRef &&
        //     this.overlayDir.overlayRef.overlayElement) {
        //     this.overlayDir.overlayRef.overlayElement.style.fontSize = `${this._triggerFontSize}px`;
        //   }
        // });
    }
    
    /** Closes the menu panel and focuses the host element. */
    close(): void {
        if (this._menuOpen) {
            this._menuOpen = false;
            this._changeDetectorRef.markForCheck();
            
            this.overlayRef.dispose();
            this.onBlur()
        }
    }
    
    protected getOverlayConfig(): OverlayConfig {
        const positionStrategy = this._overlay.position()
                .flexibleConnectedTo(this._menuTrigger.elementRef.nativeElement)
                .withPush(false)
                .withPositions(this.getPositions());
        
        return new OverlayConfig({
            width: this._menuTrigger.elementRef.nativeElement.clientWidth,
            positionStrategy: positionStrategy
        });
    }
    
    private getPositions(): ConnectionPositionPair[] {
        return [{
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
        }, {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
        }];
    }
    
    
    private _loadTemplate = (slot: AzoraSlot) => {
        slot.contentRendered
                .pipe(untilDestroyed(this))
                .subscribe((state: SlotState) => {
                    this.templates[state.name] = state.template;
                    this.cd.detectChanges();
                });
    };
    
    
    onToggleMenu = () => {
        this.show_menu = !this.show_menu;
    };
    
    
    onChange = (opt: SelectOption) => {
        this.selected_option = opt
        // this.options = _.map(this.options, (item: { value: any, label: any, selected: Boolean }) => {
        //     item.selected = _.isEqual(item, option);
        //     this.selected_option = item;
        //     return item;
        // });
        // this.show_menu = false;
        this.change.emit(opt.value);
        this.control.setValue(opt.value)
        this.close()
    };
    
    ///-----------------------------------------------  Host   -----------------------------------------------///
    
    // @HostListener('document:click', ['$event'])
    // clickout(event) {
    //   if (!this.eRef.nativeElement.contains(event.target)) {
    //     this.show_menu = false;
    //   }
    // }
    _showAppendContent(): boolean {
        const portalNames = _.reduce(this.templates, (result: string[], value, key) => {
            if (value && ['append', 'feedback_icons'].includes(key)) result.push(key);
            return result;
        }, []);
        
        return portalNames.length > 0;
    }
    
    _showClearableContent(): boolean {
        return this.control.props.clearable && this.control.value;
    }
    
}
